import { AuthProvider, useSessionRefresh } from '@smartsash/auth';
import { RouterProvider } from '@tanstack/react-router';
import { I18nextProvider } from 'react-i18next';

import i18n from '@/i18n';
import { router } from '@/router';
import { authClient } from '@/services/auth';
import { SessionPermissionsProvider } from '@/ui/App/SessionPermissionsProvider';
import { UiProviders } from '@/ui/App/UiProviders';

/**
 * HS-859: the `AuthRouterBridge` is gone. It existed to copy an auth snapshot into the router
 * context (`router.update` + `invalidate`) on every change, because guards ran outside React
 * and could not see the provider. Router and components now share the same live `AuthClient`,
 * so there is nothing to synchronise.
 */
/**
 * Keeps the session's permission snapshot current (HS-1387). Renders nothing.
 *
 * It is its own component rather than a hook call inside `SessionPermissionsProvider` because
 * the two jobs are separate: that provider *publishes* the permissions, this one decides *when
 * they are re-read*. Folding the schedule into the provider would also drag a live
 * `QueryClientProvider` into every unit test of a component whose whole point is that it can be
 * rendered with a stubbed session.
 *
 * Must sit inside `AuthProvider` (it reads the session) and inside `QueryClientProvider`
 * (`main.tsx` supplies that above `App`).
 */
function SessionRefresh() {
  useSessionRefresh();
  return null;
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      {/* Above `AuthProvider`: the UI seams need translations, not the session. */}
      <UiProviders>
        <AuthProvider auth={authClient}>
          <SessionRefresh />
          <SessionPermissionsProvider>
            <RouterProvider router={router} context={{ auth: authClient }} />
          </SessionPermissionsProvider>
        </AuthProvider>
      </UiProviders>
    </I18nextProvider>
  );
}

export default App;
